// src/components/catalog/pickers/SalesItemPicker.jsx
//
// Combined product/service picker for Sales Documents.
// =============================================================================
//
// PURPOSE
// -------
// Reception users should not need to remember whether an item is technically a
// product or a service before searching. In daily work they think: "add shampoo"
// or "add massage 60 min". This component searches both catalog areas and lets
// the Sales page convert the selection into the correct line type.
//
// UX MODEL
// --------
// - Quick search: fast enough for daily reception work and barcode-like input.
// - Browse modal: always available for discovery when the user does not know the
//   exact catalog names. The modal has Products and Services tabs.
//
// ARCHITECTURAL BOUNDARY
// ----------------------
// This component never posts documents and never changes stock. Product lines
// create stock issues later only when salesDocumentService.postDocument() calls
// stockMovementService on the backend.
//
// I18N NOTE
// ---------
// Only user-facing UI text is translated here. Technical item type values remain
// stable English codes in JavaScript/backend payloads: product and service.
//
// Even compact labels such as service duration are routed through i18n. Today EN
// and PL both use "min", but keeping it behind a key prevents future language
// additions from requiring another component-level refactor.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import "./CatalogPicker.css";

// Formatting helpers are UI-only. Backend totals, VAT and stock effects remain
// authoritative in the sales/stock services.
function formatDecimal(value, digits = 3) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

function formatMoney(value, currency = "PLN") {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)} ${currency}` : String(value);
}

function formatDurationMinutes(value, t) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return t("catalog:picker.durationMinutes", { value: n });
}

// Display helpers tolerate partial product/service rows because quick search and
// browse endpoints may return different projections for performance reasons.
function productName(product, t) {
  return product?.name_public || product?.name || t("catalog:picker.unnamedProduct");
}

function serviceName(service, t) {
  return service?.name_public || service?.name || t("catalog:picker.unnamedService");
}

function productUnit(product) {
  return product?.stockUnit?.symbol || product?.stock_unit_code || "pcs";
}

function productSkuLine(product, t) {
  const parts = [
    product?.sku ? t("catalog:picker.sku", { value: product.sku }) : null,
    product?.barcode ? t("catalog:picker.barcode", { value: product.barcode }) : null,
    product?.brand ? t("catalog:picker.brand", { value: product.brand }) : null,
  ].filter(Boolean);

  return parts.join(" · ") || t("catalog:picker.noSkuBarcode");
}

function productCategory(product) {
  return product?.category?.name_public || product?.category?.name || product?.category_name || "—";
}

function serviceMeta(service, t) {
  const parts = [
    service?.service_code ? t("catalog:picker.serviceCode", { value: service.service_code }) : null,
    service?.section?.name_public || service?.section?.name || null,
    service?.duration_minutes ? formatDurationMinutes(service.duration_minutes, t) : null,
  ].filter(Boolean);

  return parts.join(" · ") || t("catalog:picker.serviceFallback");
}

function quantityWithUnit(quantity, product) {
  return `${formatDecimal(quantity || 0)} ${productUnit(product)}`;
}

/**
 * SalesItemPicker component.
 *
 * It centralizes the reception workflow for adding either products or services to
 * a document. Parent Sales pages decide how selected rows become document lines;
 * this picker remains a reusable UI component.
 */
const SalesItemPicker = ({
  disabled = false,
  currency = "PLN",
  stockLocationId = "",
  searchProducts,
  searchServices,
  onSelectProduct,
  onSelectService,
}) => {
  const { t } = useTranslation(["common", "catalog"]);

  const [query, setQuery] = useState("");
  const [modalQuery, setModalQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("products");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Search products and services together so reception users can type once and
  // decide from the results instead of choosing the technical item type first.
  async function runSearch(nextQuery = query, { modal = false } = {}) {
    setError("");
    setLoading(true);

    try {
      const q = String(nextQuery || "").trim() || undefined;
      const [productRows, serviceRows] = await Promise.all([
        searchProducts({ q, location_id: stockLocationId || undefined, limit: modal ? 100 : 10 }),
        searchServices({ q, limit: modal ? 100 : 10 }),
      ]);

      setProducts(Array.isArray(productRows) ? productRows : []);
      setServices(Array.isArray(serviceRows) ? serviceRows : []);
    } catch (err) {
      setError(err?.message || t("catalog:picker.catalogSearchFailed"));
    } finally {
      setLoading(false);
    }
  }

  function openModal() {
    setModalQuery(query);
    setModalOpen(true);
    runSearch(query, { modal: true });
  }

  // Product/service selection intentionally uses two callbacks. This keeps the
  // item type explicit for the parent Sales document editor.
  function chooseProduct(product) {
    onSelectProduct?.(product);
    setQuery("");
    setProducts([]);
    setServices([]);
    setModalOpen(false);
  }

  function chooseService(service) {
    onSelectService?.(service);
    setQuery("");
    setProducts([]);
    setServices([]);
    setModalOpen(false);
  }

  const hasQuickResults = products.length > 0 || services.length > 0;

  return (
    <div className="catalog-picker catalog-picker--sales-item">
      <label className="catalog-picker__label">
        {t("catalog:picker.salesItemLabel")}
        <form className="catalog-picker__row" onSubmit={(event) => { event.preventDefault(); runSearch(query); }}>
          <input value={query} disabled={disabled} placeholder={t("catalog:picker.salesItemPlaceholder")} onChange={(event) => setQuery(event.target.value)} />
          <button type="submit" className="btn btn-outline-primary" disabled={disabled || loading}>{loading ? t("catalog:picker.searching") : t("catalog:picker.search")}</button>
          <button type="button" className="btn btn-outline-secondary" disabled={disabled} onClick={openModal}>{t("catalog:picker.browseProductsServices")}</button>
        </form>
      </label>

      <div className="catalog-picker__hint">{t("catalog:picker.salesItemHint")}</div>
      {error && <div className="catalog-picker__error">{error}</div>}

      {hasQuickResults && (
        <div className="catalog-picker__quick-results" aria-label={t("catalog:picker.salesQuickResultsAriaLabel")}>
          {products.map((product) => (
            <button key={`product-${product.id}`} type="button" className="catalog-picker__quick-button" disabled={disabled} onClick={() => chooseProduct(product)}>
              <span className="catalog-picker__quick-main"><strong>{productName(product, t)}</strong><span>{productSkuLine(product, t)}</span></span>
              <span className="catalog-picker__quick-sub"><span>{t("catalog:picker.productType")} · {productCategory(product)}</span><span>{t("catalog:picker.availableWithValue", { value: quantityWithUnit(product.available_quantity ?? product.global_stock_quantity ?? 0, product) })}</span></span>
              <span className="catalog-picker__quick-tail"><span>{formatMoney(product.price_gross, currency)}</span></span>
            </button>
          ))}

          {services.map((service) => (
            <button key={`service-${service.id}`} type="button" className="catalog-picker__quick-button" disabled={disabled} onClick={() => chooseService(service)}>
              <span className="catalog-picker__quick-main"><strong>{serviceName(service, t)}</strong><span>{serviceMeta(service, t)}</span></span>
              <span className="catalog-picker__quick-sub"><span>{t("catalog:picker.serviceType")}</span><span>{formatDurationMinutes(service.duration_minutes, t)}</span></span>
              <span className="catalog-picker__quick-tail"><span>{formatMoney(service.price_gross, currency)}</span></span>
            </button>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="catalog-picker__modal-backdrop" role="presentation">
          <div className="catalog-picker__modal" role="dialog" aria-modal="true" aria-label={t("catalog:picker.browseItemsAriaLabel")}>
            <div className="catalog-picker__modal-header">
              <div><h3>{t("catalog:picker.browseItemsTitle")}</h3><p className="catalog-picker__meta">{t("catalog:picker.browseItemsHelp")}</p></div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>{t("common:actions.close")}</button>
            </div>

            <form className="catalog-picker__modal-toolbar" onSubmit={(event) => { event.preventDefault(); runSearch(modalQuery, { modal: true }); }}>
              <input value={modalQuery} disabled={loading} placeholder={t("catalog:picker.searchItemsPlaceholder")} onChange={(event) => setModalQuery(event.target.value)} />
              <button type="submit" className="btn btn-outline-primary" disabled={loading}>{loading ? t("catalog:picker.searching") : t("catalog:picker.search")}</button>
              <div className="catalog-picker__tabs" role="tablist" aria-label={t("catalog:picker.itemTypeTabsAriaLabel")}>
                <button type="button" className={`catalog-picker__tab ${activeTab === "products" ? "catalog-picker__tab--active" : ""}`} onClick={() => setActiveTab("products")}>{t("catalog:picker.productsTab", { count: products.length })}</button>
                <button type="button" className={`catalog-picker__tab ${activeTab === "services" ? "catalog-picker__tab--active" : ""}`} onClick={() => setActiveTab("services")}>{t("catalog:picker.servicesTab", { count: services.length })}</button>
              </div>
            </form>

            <div className="catalog-picker__modal-body">
              {activeTab === "products" ? (
                <table className="catalog-picker__table">
                  <thead><tr><th>{t("catalog:picker.columns.product")}</th><th>{t("catalog:picker.columns.skuBarcode")}</th><th>{t("catalog:picker.columns.category")}</th><th className="catalog-picker__right">{t("catalog:picker.columnsSales.available")}</th><th>{t("catalog:picker.columns.unit")}</th><th className="catalog-picker__right">{t("catalog:picker.columnsSales.grossPrice")}</th><th></th></tr></thead>
                  <tbody>
                    {products.length === 0 && !loading && <tr><td colSpan="7" className="catalog-picker__empty">{t("catalog:picker.noProducts")}</td></tr>}
                    {products.map((product) => (
                      <tr key={product.id}><td><strong>{productName(product, t)}</strong></td><td>{productSkuLine(product, t)}</td><td>{productCategory(product)}</td><td className="catalog-picker__right">{quantityWithUnit(product.available_quantity ?? product.global_stock_quantity ?? 0, product)}</td><td>{productUnit(product)}</td><td className="catalog-picker__right">{formatMoney(product.price_gross, currency)}</td><td className="catalog-picker__right"><button type="button" className="btn btn-sm btn-primary" onClick={() => chooseProduct(product)}>{t("catalog:picker.add")}</button></td></tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="catalog-picker__table">
                  <thead><tr><th>{t("catalog:picker.columnsSales.service")}</th><th>{t("catalog:picker.columnsSales.codeSection")}</th><th>{t("catalog:picker.columnsSales.duration")}</th><th className="catalog-picker__right">{t("catalog:picker.columnsSales.grossPrice")}</th><th className="catalog-picker__right">{t("catalog:picker.columnsSales.vatPercent")}</th><th></th></tr></thead>
                  <tbody>
                    {services.length === 0 && !loading && <tr><td colSpan="6" className="catalog-picker__empty">{t("catalog:picker.noServices")}</td></tr>}
                    {services.map((service) => (
                      <tr key={service.id}><td><strong>{serviceName(service, t)}</strong></td><td>{serviceMeta(service, t)}</td><td>{formatDurationMinutes(service.duration_minutes, t)}</td><td className="catalog-picker__right">{formatMoney(service.price_gross, currency)}</td><td className="catalog-picker__right">{service.vat_rate_percent ?? "—"}</td><td className="catalog-picker__right"><button type="button" className="btn btn-sm btn-primary" onClick={() => chooseService(service)}>{t("catalog:picker.add")}</button></td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="catalog-picker__modal-footer"><span>{t("catalog:picker.salesFooterCount", { products: products.length, services: services.length })}</span><span>{t("catalog:picker.salesFooterHint")}</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesItemPicker;


